/**
 * The app-side emit host for the 90-day trial-end experience — the recap modal,
 * the ending-soon reminder popover and the post-trial-end discreet card
 * (`@heidi/commerce`).
 *
 * Ruling 24: the domain side decides WHEN each event fires, snapshots the
 * context the two-hour auto-dismiss timer outlives, and builds every
 * `intl`-formatted label; these functions add the `event_name`, the destination
 * routing, and nothing else.
 *
 * ROUTING IS PRESERVED, NOT TIDIED, and it is NOT uniform across the seven
 * emits — which is the reason each one is asserted separately in the suite. The
 * three views pass `{ amplitude: false }`; the auto-dismiss and all six clicks
 * pass no `options` and reach every destination. That asymmetry is what the
 * surfaces have always sent. None of `modal_viewed` / `modal_dismissed` /
 * `button_clicked` is on the Snowplow denylist
 * (`@/lib/analytics/snowplow-suppression`), so no emit here carries a
 * `snowplow: false` and none should acquire one: the denylist is applied by
 * event NAME inside the pipeline's `enrich()`.
 *
 * MODULE-LEVEL FUNCTIONS ON PURPOSE. Each `…Viewed` binding ends up a dependency
 * of its surface's open effect, so an emit whose identity changed per render
 * would re-fire it and double-count the view.
 */
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN } from '@/models/analytics/features/trialsAndUpgradesScreenNames';
import {
  NINETY_DAY_TRIAL_END_DISCREET_ACTION,
  NINETY_DAY_TRIAL_ENDING_REMINDER_ACTION,
  NINETY_DAY_TRIAL_RECAP_ACTION,
  type NinetyDayTrialEndDiscreetActionToken,
  type NinetyDayTrialEndDiscreetAnalyticsPorts,
  type NinetyDayTrialEndingReminderActionToken,
  type NinetyDayTrialEndingReminderAnalyticsPorts,
  type NinetyDayTrialRecapActionToken,
  type NinetyDayTrialRecapAnalyticsPorts,
  type TrackNinetyDayTrialContactSalesClick,
  type TrackNinetyDayTrialEndDiscreetClick,
  type TrackNinetyDayTrialEndDiscreetViewed,
  type TrackNinetyDayTrialEndingReminderClick,
  type TrackNinetyDayTrialEndingReminderDismissed,
  type TrackNinetyDayTrialEndingReminderViewed,
  type TrackNinetyDayTrialRecapClick,
  type TrackNinetyDayTrialRecapViewed,
} from '@heidi/commerce/contracts';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';

/** Snowplow-first, matching what the three views have always sent. */
const VIEW_TRACK_OPTIONS = { amplitude: false } as const;

/**
 * Each surface's `action` tokens bound to the app enum members that declare them.
 *
 * Three maps rather than one, because the three surfaces are three screens with
 * three action vocabularies; a merged map would let a reminder token be emitted
 * against the recap screen and still type-check.
 */
const RECAP_ACTION_BY_TOKEN = {
  [NINETY_DAY_TRIAL_RECAP_ACTION.upgrade]: ButtonAction.NinetyDayTrialRecapUpgrade,
  [NINETY_DAY_TRIAL_RECAP_ACTION.contactSales]:
    ButtonAction.NinetyDayTrialRecapContactSales,
  [NINETY_DAY_TRIAL_RECAP_ACTION.remindMeLater]:
    ButtonAction.NinetyDayTrialRecapRemindMeLater,
} as const satisfies Record<NinetyDayTrialRecapActionToken, ButtonAction>;

const REMINDER_ACTION_BY_TOKEN = {
  [NINETY_DAY_TRIAL_ENDING_REMINDER_ACTION.seeSummary]:
    ButtonAction.NinetyDayTrialEndingReminderSeeSummary,
  [NINETY_DAY_TRIAL_ENDING_REMINDER_ACTION.close]:
    ButtonAction.NinetyDayTrialEndingReminderClose,
} as const satisfies Record<NinetyDayTrialEndingReminderActionToken, ButtonAction>;

const DISCREET_ACTION_BY_TOKEN = {
  [NINETY_DAY_TRIAL_END_DISCREET_ACTION.learnMore]:
    ButtonAction.NinetyDayTrialEndDiscreetLearnMore,
  [NINETY_DAY_TRIAL_END_DISCREET_ACTION.close]:
    ButtonAction.NinetyDayTrialEndDiscreetClose,
} as const satisfies Record<NinetyDayTrialEndDiscreetActionToken, ButtonAction>;

/** The recap modal became visible. The once-per-open guard runs in the domain hook. */
export const trackNinetyDayTrialRecapViewed: TrackNinetyDayTrialRecapViewed = ({
  screen_name,
  ...teamContext
}) => {
  track({
    options: VIEW_TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
      ...teamContext,
    },
  });
};

/** One of the recap modal's three CTAs was clicked. */
export const trackNinetyDayTrialRecapClick: TrackNinetyDayTrialRecapClick = ({
  screen_name,
  action,
  ...rest
}) => {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
      action: RECAP_ACTION_BY_TOKEN[action],
      ...rest,
    },
  });
};

/** The ending-soon reminder popover became visible. */
export const trackNinetyDayTrialEndingReminderViewed: TrackNinetyDayTrialEndingReminderViewed =
  ({ screen_name, ...context }) => {
    track({
      options: VIEW_TRACK_OPTIONS,
      event_name: TrackingEventName.ModalViewed,
      props: {
        screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
        ...context,
      },
    });
  };

/**
 * The reminder popover timed out without engagement.
 *
 * No `{ amplitude: false }`, unlike the view directly above it: the auto-dismiss
 * has always reached Amplitude too. Preserved rather than made consistent with
 * its sibling — suppressing it here would break whatever reads it there.
 */
export const trackNinetyDayTrialEndingReminderDismissed: TrackNinetyDayTrialEndingReminderDismissed =
  ({ screen_name, ...context }) => {
    track({
      event_name: TrackingEventName.ModalDismissed,
      props: {
        screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
        ...context,
      },
    });
  };

/** One of the reminder popover's two CTAs was clicked. */
export const trackNinetyDayTrialEndingReminderClick: TrackNinetyDayTrialEndingReminderClick =
  ({ screen_name, action, ...rest }) => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
        action: REMINDER_ACTION_BY_TOKEN[action],
        ...rest,
      },
    });
  };

/** The post-trial-end discreet card became visible. */
export const trackNinetyDayTrialEndDiscreetViewed: TrackNinetyDayTrialEndDiscreetViewed =
  ({ screen_name }) => {
    track({
      options: VIEW_TRACK_OPTIONS,
      event_name: TrackingEventName.ModalViewed,
      props: { screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name] },
    });
  };

/** One of the discreet card's two CTAs was clicked. */
export const trackNinetyDayTrialEndDiscreetClick: TrackNinetyDayTrialEndDiscreetClick = ({
  screen_name,
  action,
  ...rest
}) => {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
      action: DISCREET_ACTION_BY_TOKEN[action],
      ...rest,
    },
  });
};

/**
 * The 90-day variant's contact-sales CTA, one binding per ORIGIN modal.
 *
 * The shared `useNinetyDayTrialVariant` hook renders this CTA inside two modals
 * that belong to other roots, each reporting its own screen and its own action
 * (`upgrade_to_clinician_modal` / `clinician_upgrade_modal_contact_sales` and the
 * practice pair). The hook only knows the plan-name copy it formats, so the
 * screen and the action are bound HERE, once per origin, and the hook takes the
 * bound emitter as a port — rather than the two enum values as parameters, which
 * is what used to pull `@/models/analytics/features/ui` into the domain hook.
 *
 * Two named module constants rather than a factory keyed by screen: a factory
 * called during render would mint a new identity every time, and the hook's
 * `handleContactSalesClick` depends on this port.
 */
const contactSalesEmitter =
  (screenName: ScreenName, action: ButtonAction): TrackNinetyDayTrialContactSalesClick =>
  ({ label }) => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: { screen_name: screenName, action, label },
    });
  };

export const trackClinicianUpgradeModalContactSalesClick: TrackNinetyDayTrialContactSalesClick =
  contactSalesEmitter(
    ScreenName.UpgradeToClinicianModal,
    ButtonAction.ClinicianUpgradeModalContactSales
  );

export const trackPracticeUpgradeModalContactSalesClick: TrackNinetyDayTrialContactSalesClick =
  contactSalesEmitter(
    ScreenName.UpgradeToPracticeModal,
    ButtonAction.PracticeUpgradeModalContactSales
  );

/**
 * Each surface's seams as one bundle.
 *
 * Module constants rather than `useMemo`s, so each is the same object on every
 * render and cannot be the thing that re-fires an open effect.
 */
export const ninetyDayTrialRecapAnalyticsPorts: NinetyDayTrialRecapAnalyticsPorts = {
  trackViewed: trackNinetyDayTrialRecapViewed,
  trackClick: trackNinetyDayTrialRecapClick,
};

export const ninetyDayTrialEndingReminderAnalyticsPorts: NinetyDayTrialEndingReminderAnalyticsPorts =
  {
    trackViewed: trackNinetyDayTrialEndingReminderViewed,
    trackDismissed: trackNinetyDayTrialEndingReminderDismissed,
    trackClick: trackNinetyDayTrialEndingReminderClick,
  };

export const ninetyDayTrialEndDiscreetAnalyticsPorts: NinetyDayTrialEndDiscreetAnalyticsPorts =
  {
    trackViewed: trackNinetyDayTrialEndDiscreetViewed,
    trackClick: trackNinetyDayTrialEndDiscreetClick,
  };
